/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-undef */
/* eslint-disable prettier/prettier */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { config } from 'config/config';
import { COOKIES } from '@/utils/constants';
import { createCookieBig, createUrlIdc, getQueryParameter, sendDispatchEvent } from '@/utils/utils';
import { getEncryptedCookie, setEncryptedCookie } from '../utils/cookies';
import { parseTrackingData } from '../tracking/global/helpers/parseTrackingData';
import { toKebabCase } from '../utils/transformString';
import { DATALAYER_MODELS } from '../tracking/pages';
import { getMCMIDFromCookie } from './useTargetApi/getVisitorData';

const conditionalRewriteObjectKey = (obj, callback) => {
  if (!obj) return;

  Object.entries(obj).forEach(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      conditionalRewriteObjectKey(value, callback);
    } else {
      callback(key, value, obj);
    }
  });
};

const objectParse = objectString => {
  const newObj = JSON.parse(objectString);

  conditionalRewriteObjectKey(newObj, (key, value, currentObject) => {
    if (value === null) {
      // eslint-disable-next-line no-param-reassign
      currentObject[key] = undefined;
    }
  });

  return newObj;
};

const objectStringify = object => JSON.stringify(object, (k, v) => v === undefined ? null : v);

const WM_FINANCING_DATA = 'WmFinancingData';

export const getFinancingDataItem = adId => {
  const storage = localStorage.getItem(WM_FINANCING_DATA);

  if (!storage) return null;

  const storageParsed = objectParse(storage);

  if (!storageParsed?.length) return null;

  return storageParsed?.find(adVehicleData => Number(adVehicleData.adId) === Number(adId))?.data;
};

export const removeFinancingDataItem = adId => {
  if (!getFinancingDataItem(adId)) return;

  const storageParsed = objectParse(localStorage.getItem(WM_FINANCING_DATA));

  if (!storageParsed?.length) return;

  const hydratedData = objectStringify(storageParsed?.filter(adVehicleData => Number(adVehicleData.adId) !== Number(adId)));

  localStorage.setItem(WM_FINANCING_DATA, hydratedData);
};

export const addFinancingDataItem = (adId, data) => {
  if (getFinancingDataItem(adId)) removeFinancingDataItem(adId);

  const storage = localStorage.getItem(WM_FINANCING_DATA);
  const storageParsed = storage && objectParse(storage);
  const storageData = Array.isArray(storageParsed) ? storageParsed : [];

  storageData.push({ adId, data, createdAt: new Date().getTime() });

  localStorage.setItem(WM_FINANCING_DATA, objectStringify(storageData));
};

export const clearFinancingData = () => {
  localStorage.removeItem(WM_FINANCING_DATA);
};

export const clearExpiredFetchedVehicleItems = (adId, callback = () => {}) => {
  let currentAdIsExpired = false;
  const storageParsed = objectParse(localStorage.getItem(WM_FINANCING_DATA));

  if (!storageParsed?.length) return;

  const notExpiredData = objectStringify(
    storageParsed?.filter(adVehicleData => {
      const diff = Math.abs(new Date(adVehicleData.createdAt) - new Date());
      const diffMinutes = Math.floor((diff / (1000 * 60 * 60)) * 60);
      const isNotExpired = diffMinutes < 55;

      currentAdIsExpired = Number(adId) === Number(adVehicleData.adId) && !isNotExpired;

      return isNotExpired;
    })
  );

  if (!notExpiredData.length) clearFinancingData();

  localStorage.setItem(WM_FINANCING_DATA, notExpiredData);

  if (currentAdIsExpired) callback();
};

const useFinancing = ({ adId, userIsAuth }) => {
  const [newsletter, setNewsletter] = useState(false);
  const [localCookie, setLocalCookie] = useState({});
  const [pageDataLayerObject, updatePageDataLayerObject] = useState(null);
  const [trackingEventQueue, setTrackingEventQueue] = useState([]);

  let trackingEventQueueTimer;

  const financingForm = useMemo(() => getFinancingDataItem(adId), [adId]);

  const DEFAULT_PHOTO = 'https://webmotors.com.br/assets/img/webp/vehicle_no_photo.webp?t=1.0.1';

  const PERSON_TYPE = {
    PJ: 2,
    PF: 1,
  };

  const VEHICLE_TYPE = {
    CAR: 4,
    MOTO: 5,
  };

  const OFFERS_TYPE = {
    YES: 1,
    NO: 2,
  };

  const INTEREST_TYPE = {
    YES: 1,
    NO: 2,
  };

  const CARDELIVERY_TYPE = {
    YES: 1,
    NO: 2,
  };

  const EXPIRE_TIME = 0.3;

  const getAdobeGuid = () => {
    let adobeGuid = null;
    if (
      process.env.NODE_ENV !== 'development' &&
      typeof Visitor !== 'undefined' &&
      Visitor != null &&
      Visitor.getInstance('3ADD33055666F1A47F000101@AdobeOrg') !== null &&
      Visitor.getInstance('3ADD33055666F1A47F000101@AdobeOrg') !== undefined &&
      Visitor.getInstance(
        '3ADD33055666F1A47F000101@AdobeOrg'
      ).getMarketingCloudVisitorID() != null &&
      Visitor.getInstance(
        '3ADD33055666F1A47F000101@AdobeOrg'
      ).getMarketingCloudVisitorID() !== undefined
    ) {
      adobeGuid = Visitor.getInstance(
        '3ADD33055666F1A47F000101@AdobeOrg'
      ).getMarketingCloudVisitorID()
    };

    if (!adobeGuid) {
      adobeGuid = getMCMIDFromCookie()
    }
    return adobeGuid;
  };

  const getLeadCookie = () => {
    const cookieData = getEncryptedCookie(COOKIES.FORM_DATA);
    if (!cookieData) return {};
    const { email, fullName, numberPhone } = cookieData.Principal;
    return {
      email,
      fullName,
      phone: numberPhone,
    };
  };

  const createFinancingLeadCookie = data => {
    const defaultCookie = { Principal: {}, Details: {} };
    setNewsletter(data.newsletter);

    const cookieData = getEncryptedCookie(COOKIES.FORM_DATA) || defaultCookie;

    const newCookieData = {
      ...cookieData,
      Principal: {
        ...cookieData.Principal,
        email: data.email,
        fullName: data.fullName,
        numberPhone: data.phone,
      },
      Details: {
        ...cookieData.Details,
        birthday: data.date,
        cpf: data.cpf,
        location: data.state.value,
        newsletter: data.newsletter,
        vehicleInterest: false,
        leadSeeInstallments: data.leadSeeInstallments,
      },
    };

    setEncryptedCookie(COOKIES.FORM_DATA, newCookieData);
  };

  const getPaymentType = paymentFormId => {
    if (!paymentFormId || paymentFormId === 7) return 'boleto';

    return 'debito-em-conta-santander';
  }

  const getInsuranceDescription = (data, insuranceId) => {
    if (!insuranceId) {
      return "sem-seguro";
    }

    return toKebabCase(data?.identification?.response?.atxResponse?.insurances?.find((insurance) => {
      return insurance?.id === Number(insuranceId)
    })?.description);
  }

  const generateDataLayerObject = useCallback((data = localCookie) => {
    const { href, search } = window?.location;

    const baseDataLayer = parseTrackingData(
      DATALAYER_MODELS.financing,
      data
    ).datalayer

    return {
      ...financingForm?.datalayer,
      site: {
        ...financingForm?.datalayer?.site,
        ...baseDataLayer?.site,
        brandOrigin: getQueryParameter(search, 'origem') || 'webmotors',
      },
      page: {
        ...pageDataLayerObject,
        url: href,
        flowType: "fluxo-comprar",
        trackingCode: {
          idcmp: getQueryParameter(search, 'idcmp'),
          idcmpint: getQueryParameter(search, 'idcmpint'),
          icid: getQueryParameter(search, 'icid'),
          lkid: getQueryParameter(search, 'lkid'),
        },
      },
      user: {
        ...financingForm?.datalayer?.user,
        loginStatus: userIsAuth ? 1 : 2,
        birthDate: data?.customer?.birthday,
        santanderAccount: data?.identification?.response?.atxResponse?.fees?.tcExemption
          ? '1'
          : '2',
      },
      ...data?.simulation && {
        conversion: {
          financing: {
            simulationID: data?.simulation?.response?.uuid || undefined,
            optin: {
              interest: data?.leadSeeInstallments ? INTEREST_TYPE.YES : INTEREST_TYPE.NO,
              offers: newsletter ? OFFERS_TYPE.YES : OFFERS_TYPE.NO,
            },
            ...data?.error && {
              errorID: data?.error?.data?.StatusCode || data?.error?.status || undefined,
              errorDescription: data?.error?.data?.Messages && data?.error?.data?.Messages[0] || data?.data?.Message || data?.error?.data?.message || undefined,
            },
            entryValue: data?.simulation?.response?.downPayment || undefined,
            releasedValue: data?.simulation?.response?.releasedValue || undefined,
            installmentValue: data?.simulation?.response?.installmentValue || undefined,
            installmentNumber: data?.simulation?.response?.installmentAmount || undefined,
            financialProtection: getInsuranceDescription(data, data?.simulation?.response?.insuranceId) || undefined,
            paymentType: getPaymentType(data?.simulation?.response?.paymentFormId),
            ...(data?.proposal?.InternalID || data?.proposal?.ExternalID || data?.proposal?.error) && {
              proposal: {
                ...data?.proposal?.error && {
                  errorID: data?.proposal?.error?.errorID || undefined,
                  errorDescription: data?.proposal?.error?.errorDescription || undefined,
                },
                InternalID: data?.proposal?.InternalID || undefined,
                ExternalID: data?.proposal?.ExternalID || undefined,
              }
            },
          }
        }
      }
    }
  }, [newsletter, userIsAuth, localCookie, financingForm, pageDataLayerObject]);

  const updateObjDatalayer = (dataLayer = generateDataLayerObject()) => {
    objDataLayer = dataLayer;
  };

  const setDataForDataLayer = ({ simulation }) => {
    const hasDataLayer = JSON.parse(localStorage.getItem('WMLoanDataLayer'));

    const entryValue = { value: simulation?.response?.downPayment };
    const releasedValue = { value: simulation?.response?.releasedValue };
    const installmentValue = { value: simulation?.response?.installmentValue };

    const localDataLayerObject = generateDataLayerObject(localCookie);

    let dataLayer = {};
    const objDataLayerLP = {
      ...localDataLayerObject,
      conversion: {
        ...localDataLayerObject?.conversion,
        financing: {
          ...localDataLayerObject?.conversion?.financing,
          entryValue,
          releasedValue,
          installmentValue,
        },
      },
    };

    dataLayer = { ...objDataLayerLP };
    
    if (hasDataLayer) {
      dataLayer = { ...hasDataLayer.dataLayer, ...objDataLayerLP };
    };

    window?.localStorage?.setItem('WMLoanDataLayer', JSON.stringify({ dataLayer }));
  };

  const mergeLocalCookieWithData = useCallback((data) => ({
    ...localCookie,
    ...data,
    proposal: {
      ...localCookie?.proposal,
      ...data?.proposal,
    },
  }), [localCookie]);

  const trackingCookies = useCallback((data) => {
    const localData = mergeLocalCookieWithData(data);
    const localDataLayerObject = generateDataLayerObject(localData);

    setLocalCookie(localData);
    updateObjDatalayer(localDataLayerObject);

    if (data?.error) return;

    setDataForDataLayer(localData);
    createCookieBig(
      COOKIES.CONTINUE_FINANCING,
      { service: localData },
      EXPIRE_TIME
    );
  }, [localCookie, pageDataLayerObject]);

  const addTrackingEventToQueue = useCallback((event, data) => {
    setTrackingEventQueue(previousEventQueue => [...previousEventQueue, { event, data }]);
  }, [trackingEventQueue]);

  const peekTrackingEventFromQueue = useCallback(() => trackingEventQueue[0], [trackingEventQueue]);

  const executeTrackingEventQueue = useCallback(() => {
    if (!trackingEventQueue.length) {
      return clearInterval(trackingEventQueueTimer);
    }

    trackingEventQueueTimer = !trackingEventQueueTimer && setInterval(() => {
      const { event, data } = peekTrackingEventFromQueue();

      trackingCookies(data);
      sendDispatchEvent(event);

      setTrackingEventQueue(previousEventQueue => previousEventQueue.slice(1));
    }, 1000);

    return trackingEventQueueTimer;
  }, [trackingCookies, trackingEventQueue]);

  const trackingEvents = (event, data) => addTrackingEventToQueue(event, data);

  useEffect(() => {
    executeTrackingEventQueue();

    return () => clearInterval(trackingEventQueueTimer);
  }, [trackingEventQueue]);

  const seeConditions = (sellerType) => {
    const sellerTypeName = 'aprovacao';
    const tagurl = `&utm_medium=calhau&utm_source=webmotors&utm_campaign=financiamento&utm_content=simular-anuncio-continuarsantander-${sellerType.toLowerCase()}`;
    const urlContinueSantander = createUrlIdc(
      `${config.urlFinancig}${sellerTypeName}/`,
      undefined,
      'financiamento',
      `simular-anuncio-continuarsantander-${sellerType.toLowerCase()}${tagurl}`
    );
    sendDispatchEvent('financiamento-detalhe');
    window?.open(urlContinueSantander, '_blank');
    window?.location.reload(true);
  };

  return {
    getAdobeGuid,
    getLeadCookie,
    createFinancingLeadCookie,
    trackingEvents: useCallback(trackingEvents, []),
    trackingCookies,
    seeConditions,
    financingForm,
    localCookie,
    dataLayerObject: generateDataLayerObject(localCookie),
    pageDataLayerObject,
    updatePageDataLayerObject,
    CARDELIVERY_TYPE,
    INTEREST_TYPE,
    OFFERS_TYPE,
    EXPIRE_TIME,
    PERSON_TYPE,
    VEHICLE_TYPE,
    DEFAULT_PHOTO
  };
}

export default useFinancing;